import { useState, useCallback, type ImgHTMLAttributes } from 'react';

interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}

/**
 * 图片懒加载组件
 *
 * 优化点：
 * 1. decoding="async" — 浏览器在主线程之外解码图片，减少渲染卡顿
 * 2. 淡入动画 — 加载完成后从 opacity:0 平滑过渡到 opacity:1
 * 3. 错误兜底 — 加载失败时返回 null（隐藏图片元素）
 *
 * 不使用 wrapper div，直接增强 <img> 元素，避免破坏现有布局和样式。
 */
export default function LazyImage({ src, alt, className = '', style, onLoad, onError, ...rest }: LazyImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const handleLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    setLoaded(true);
    onLoad?.(e);
  }, [onLoad]);

  const handleError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    setError(true);
    onError?.(e);
  }, [onError]);

  if (error) return null;

  return (
    <img
      src={src}
      alt={alt}
      className={`${className} img-fade-in ${loaded ? 'img-loaded' : ''}`}
      style={style}
      decoding="async"
      loading="lazy"
      onLoad={handleLoad}
      onError={handleError}
      {...rest}
    />
  );
}
